import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Button } from '@sololearnorg/sol';
import { Container, HeartService } from '../../../../symphony';

import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlDataService } from '../../services/sl-data.service';
import {
  ActionButtonTypes,
  ActionType,
  QuizFeedbackType,
  RequestType,
} from '../../global-interface';
import { SlLEContext } from '../../global-constants';
import { QuizFeedback } from '../QuizFeedback/QuizFeedback';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';

import s from './LessonFooter.scss';

export function LessonFooter(): JSX.Element {
  useStyles(s);
  const [enterEvent, setEnterEvent] = useState<KeyboardEvent>(null);

  const { actions$ } = Container.take(SlLEContext, SlActions);

  const enterKeyHandler = (e: KeyboardEvent): void => {
    const { isShownLPComments$ } = Container.take(
      SlLEContext,
      SlDataService,
    );

    if (
      (e.code.toLowerCase() === 'enter'
        || e.code.toLowerCase() === 'numpadenter')
      && !isShownLPComments$.value
    ) {
      setEnterEvent(e);
    }
  };

  const {
    lessonFooterPrmBtn$: primaryButton,
    lessonFooterContBtn$: continueButton,
    loading$: loading,
    quizFeedback$: quizFeedback,
  } = useContainerData(SlLEContext, SlDataService, [
    'lessonFooterPrmBtn$',
    'lessonFooterContBtn$',
    'loading$',
    'quizFeedback$',
  ]);

  const primaryButtonLoading = loading?.footerPrimaryButton;

  useEffect(() => {
    document.addEventListener('keydown', enterKeyHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyHandler);
    };
  }, []);

  useEffect(() => {
    const heartService = Container.take('global', HeartService);
    const heartsRanOut = heartService?.isRanOutOfHearts();

    if (
      primaryButton
      && primaryButton.type !== ActionButtonTypes.secondary
      && !primaryButton.disabled
      && !heartsRanOut
    ) {
      actions$.next({
        type: primaryButton.action,
        requestType: RequestType.Parallel,
      });
    } else if (
      continueButton
      && continueButton.type === ActionButtonTypes.primary
      && !continueButton.disabled
      && !heartsRanOut
    ) {
      actions$.next({
        type: continueButton.action,
        requestType: RequestType.Parallel,
      });
    }
  }, [enterEvent]);

  const onPrimaryButtonClick = () => {
    actions$.next({
      type: primaryButton.action,
      requestType: RequestType.Parallel,
    });
  };

  return (
    <footer
      className={classNames('le-lesson-footer', {
        grey:
          !!quizFeedback
          || primaryButton?.action === ActionType.CheckAnswer,
      })}
      sl-test-data="cmpLessonFooter"
    >
      <QuizFeedback />
      {!quizFeedback && <div id="lessonPageOptionsPortal" />}
      {primaryButton && (
        <Button
          className={classNames('le-lesson-footer__btn-primary', {
            rightAnswer:
              quizFeedback === QuizFeedbackType.Correct
              || quizFeedback === QuizFeedbackType.Correct3InARow
              || quizFeedback === QuizFeedbackType.Correct5InARow,
            wrongAnswer:
              quizFeedback === QuizFeedbackType.Incorrect
              || quizFeedback === QuizFeedbackType.FewOptions
              || quizFeedback === QuizFeedbackType.ByPassedIncorrect,
            disabled: primaryButton?.disabled,
          })}
          size="m"
          buttonType={
            primaryButton?.disabled || !!quizFeedback
              ? ActionButtonTypes.snowflake
              : primaryButton?.type || ActionButtonTypes.primary
          }
          onClick={onPrimaryButtonClick}
          sl-test-data="btnLessonPrimaryButton"
          disabled={primaryButton?.disabled}
          loading={primaryButtonLoading}
        >
          {primaryButton?.title}
        </Button>
      )}
    </footer>
  );
}
